import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Radio } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage({ onLogin }) {
  const [userid, setUserid] = useState("");
  const [password, setPassword] = useState("");
  const [selectedAgencies, setSelectedAgencies] = useState({});
  const [agencies, setAgencies] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Iter 98 — Training sandbox toggle. When ticked, the dispatcher enters
  // demo mode: all writes route to `demo_*` collections on the backend,
  // and a fresh 5-unit ambulance roster is auto-seeded for their agency.
  const [demoMode, setDemoMode] = useState(false);

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(null);

  // Password change state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changeUserid, setChangeUserid] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [changeError, setChangeError] = useState("");
  const [changeSuccess, setChangeSuccess] = useState("");

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotUserid, setForgotUserid] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");

  useEffect(() => {
    axios.get(`${API}/agencies`).then((r) => setAgencies(r.data)).catch(() => {});
  }, []);

  // Sister-agency auto-pairing: checking BSVAC also checks CBVAC and vice
  // versa — since the two corps share personnel, a dispatcher covering one
  // is always covering both. Unchecking a sister also unchecks its pair.
  const SISTER_AGENCIES_MAP = {
    "BEDFORD STUYVESANT VOLUNTEER AMBULANCE CORPS": "CENTRAL BROOKLYN VOLUNTEER AMBULANCE CORPS",
    "CENTRAL BROOKLYN VOLUNTEER AMBULANCE CORPS": "BEDFORD STUYVESANT VOLUNTEER AMBULANCE CORPS",
  };

  const toggleAgency = (name) => {
    setSelectedAgencies((prev) => {
      const next = !prev[name];
      const updated = { ...prev, [name]: next };
      const sister = SISTER_AGENCIES_MAP[name];
      if (sister) updated[sister] = next;
      return updated;
    });
  };

  const selectedList = Object.keys(selectedAgencies).filter((k) => selectedAgencies[k]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userid || selectedList.length === 0) return;
    setError("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/dispatcher-login`, { userid, password });
      if (res.data.success) {
        // Check if must change password
        if (res.data.must_change_password) {
          setShowChangePassword(true);
          setChangeUserid(userid);
          setLoading(false);
          return;
        }
        // Show confirmation dialog
        setPendingLogin({
          username: res.data.username,
          user_id: res.data.user_id || "",
          dispatcher_userid: res.data.dispatcher_userid || userid,
          // Iter 99 — carry the user's HOME agency (assigned at account
          // creation) separately from the dispatcher-selected `agencies`
          // list. Reports are scoped to this home_agency only so an
          // operator can never pull data for another corps.
          home_agency: res.data.agency || "",
          agencies: selectedList,
          role: "dispatcher",
          permissions: res.data.permissions || [],
          certifications: res.data.certifications || [],
          // Iter 96 — carries the single-terminal session token so the
          // WS `session_kicked` broadcast can distinguish OUR client
          // from other browsers watching the same event.
          session_token: res.data.session_token || "",
          // Iter 98 — Training sandbox flag. When true, App.js attaches
          // `X-Demo-Mode: true` to every axios request and the WS URL
          // adds `?demo=1` so every operational read/write and broadcast
          // is scoped to the demo namespace.
          demo_mode: demoMode,
        });
        if (res.data.session_token) {
          localStorage.setItem("dispatcher_session_token", res.data.session_token);
        }
        setShowConfirmation(true);
      } else {
        setError(res.data.error || "Invalid credentials");
      }
    } catch {
      setError("Login failed. Please try again.");
    }
    setLoading(false);
  };

  const confirmDispatch = async () => {
    if (!pendingLogin) return;
    // Iter 98 — When entering demo mode, seed a fresh training roster for
    // this dispatcher's PRIMARY agency (first selected). This gives the
    // trainee an immediate set of 5 available ambulances to dispatch.
    if (pendingLogin.demo_mode && pendingLogin.agencies?.length) {
      try {
        await axios.post(
          `${API}/demo/start`,
          { agency: pendingLogin.agencies[0], dispatcher_userid: pendingLogin.dispatcher_userid },
          { headers: { "X-Demo-Mode": "true" } },
        );
      } catch (e) {
        // Seed failure shouldn't block login — trainee will just see an
        // empty demo. They can hit "Reset Demo" from inside the console.
        console.warn("demo seed failed:", e);
      }
    }
    onLogin(pendingLogin);
    setShowConfirmation(false);
  };

  const cancelDispatch = () => {
    setShowConfirmation(false);
    setPendingLogin(null);
  };

  const handleChangePassword = async () => {
    setChangeError("");
    setChangeSuccess("");
    if (!newPassword || !confirmNewPassword) {
      setChangeError("Please fill in both password fields");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setChangeError("Passwords do not match");
      return;
    }
    if (newPassword.length < 4) {
      setChangeError("Password must be at least 4 characters");
      return;
    }
    try {
      const res = await axios.post(`${API}/auth/change-password`, {
        userid: changeUserid,
        old_password: password,
        new_password: newPassword,
      });
      if (res.data.success) {
        setChangeSuccess("Password changed successfully. Please log in again.");
        setPassword("");
        setTimeout(() => {
          setShowChangePassword(false);
          setChangeSuccess("");
          setNewPassword("");
          setConfirmNewPassword("");
        }, 2000);
      } else {
        setChangeError(res.data.error || "Failed to change password");
      }
    } catch {
      setChangeError("Failed to change password");
    }
  };

  const handleForgotPassword = async () => {
    setForgotError("");
    setForgotSuccess("");
    if (!forgotUserid) {
      setForgotError("Please enter your User ID");
      return;
    }
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { userid: forgotUserid });
      if (res.data.success) {
        setForgotSuccess("Password has been reset to default (bsvac727). You will be required to change it on next login.");
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotSuccess("");
          setForgotUserid("");
        }, 3000);
      } else {
        setForgotError(res.data.error || "User not found");
      }
    } catch {
      setForgotError("Failed to reset password");
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 4 }}>
          <Radio size={28} color="#00BFFF" />
        </div>
        <h1 data-testid="login-title">Volunteer EMS</h1>
        <h2>Dispatcher Console</h2>

        <form onSubmit={handleSubmit}>
          <div className="login-field">
            <label htmlFor="userid">User ID</label>
            <input
              data-testid="login-username"
              id="userid"
              type="text"
              value={userid}
              onChange={(e) => setUserid(e.target.value)}
              placeholder="Enter User ID"
            />
          </div>

          <div className="login-field">
            <label htmlFor="password">Password</label>
            <input
              data-testid="login-password"
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
            />
          </div>

          <div className="login-field">
            <label>Agency Dispatching For</label>
            <div data-testid="login-agency" style={{ maxHeight: 160, overflow: "auto", border: "1px solid #333", borderRadius: 4, padding: "6px 8px", background: "rgba(0,0,0,0.3)" }}>
              {/* Iter 98 — DEMO / Training Sandbox toggle. Sits at the top
                  of the agency picker so trainers can spot it instantly.
                  When ticked, all activity in this session routes to the
                  demo namespace and never touches live dispatch data. */}
              <label data-testid="login-demo-toggle"
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "6px 8px", marginBottom: 6, cursor: "pointer",
                  fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif",
                  color: demoMode ? "#000" : "#FFCC00",
                  background: demoMode ? "#FFCC00" : "rgba(255,204,0,0.08)",
                  border: "1px dashed #FFCC00", borderRadius: 4, fontWeight: 700,
                  letterSpacing: 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={demoMode}
                  onChange={(e) => setDemoMode(e.target.checked)}
                  style={{ accentColor: "#FFCC00" }}
                  data-testid="login-demo-checkbox"
                />
                DEMO — TRAINING MODE
                <span style={{ fontSize: 10, opacity: 0.75, fontWeight: 500, letterSpacing: 0 }}>
                  (isolated sandbox — will not affect live dispatch)
                </span>
              </label>
              {agencies.map((a) => (
                <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer", fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif", color: selectedAgencies[a.name] ? "#00BFFF" : "#AAA" }}>
                  <input
                    type="checkbox"
                    checked={!!selectedAgencies[a.name]}
                    onChange={() => toggleAgency(a.name)}
                    style={{ accentColor: "#00BFFF" }}
                  />
                  {a.name}
                </label>
              ))}
              {agencies.length === 0 && <div style={{ color: "#666", fontSize: 12 }}>Loading agencies...</div>}
            </div>
          </div>

          {error && (
            <div data-testid="login-error" style={{ color: "#FF4444", fontSize: 13, textAlign: "center", marginBottom: 10, fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit"
            className="login-submit"
            disabled={loading || !userid || selectedList.length === 0}
          >
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: 12 }}>
          <button
            data-testid="forgot-password-btn"
            onClick={() => setShowForgotPassword(true)}
            style={{ background: "none", border: "none", color: "#00BFFF", fontSize: 12, cursor: "pointer", fontFamily: "'IBM Plex Sans', sans-serif", textDecoration: "underline" }}
          >
            Forgot Password?
          </button>
        </div>

        <div style={{ textAlign: "center", marginTop: 8 }}>
          <Link to="/crew" data-testid="link-crew-login" style={{ color: "#888", fontSize: 12, textDecoration: "none", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Ambulance Crew? Sign in here
          </Link>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000 }}>
          <div data-testid="dispatch-confirmation" style={{
            background: "#1a1a2e", border: "2px solid #00BFFF", borderRadius: 8,
            padding: "28px 36px", maxWidth: 440, textAlign: "center",
            fontFamily: "'IBM Plex Sans', sans-serif",
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#00BFFF", marginBottom: 16 }}>
              Confirm Dispatch Agencies
            </div>
            <div style={{ fontSize: 14, color: "#CCC", marginBottom: 12 }}>
              You are about to dispatch for the following agencies:
            </div>
            <div style={{ marginBottom: 20 }}>
              {selectedList.map((a) => (
                <div key={a} style={{ fontSize: 14, fontWeight: 600, color: "#FFF", padding: "4px 0", borderBottom: "1px solid #333" }}>
                  {a}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
              <button data-testid="confirm-dispatch-yes" onClick={confirmDispatch}
                style={{ padding: "8px 32px", background: "#00BFFF", color: "#000", border: "none", borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                Confirm
              </button>
              <button data-testid="confirm-dispatch-no" onClick={cancelDispatch}
                style={{ padding: "8px 32px", background: "#333", color: "#FFF", border: "1px solid #555", borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Dialog (first login) */}
      {showChangePassword && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000 }}>
          <div data-testid="change-password-dialog" style={{
            background: "#1a1a2e", border: "2px solid #FFCC00", borderRadius: 8,
            padding: "28px 36px", maxWidth: 400,
            fontFamily: "'IBM Plex Sans', sans-serif",
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#FFCC00", marginBottom: 8, textAlign: "center" }}>
              Password Change Required
            </div>
            <div style={{ fontSize: 13, color: "#AAA", marginBottom: 16, textAlign: "center" }}>
              You must change your password before continuing.
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 12, color: "#888", marginBottom: 4 }}>New Password</label>
              <input data-testid="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", background: "#222", border: "1px solid #444", borderRadius: 4, color: "#FFF", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, color: "#888", marginBottom: 4 }}>Confirm New Password</label>
              <input data-testid="confirm-new-password" type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", background: "#222", border: "1px solid #444", borderRadius: 4, color: "#FFF", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
            </div>
            {changeError && <div style={{ color: "#FF4444", fontSize: 13, marginBottom: 10, textAlign: "center" }}>{changeError}</div>}
            {changeSuccess && <div style={{ color: "#00CC66", fontSize: 13, marginBottom: 10, textAlign: "center" }}>{changeSuccess}</div>}
            <button data-testid="submit-change-password" onClick={handleChangePassword}
              style={{ width: "100%", padding: "10px", background: "#FFCC00", color: "#000", border: "none", borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
              Change Password
            </button>
          </div>
        </div>
      )}

      {/* Forgot Password Dialog */}
      {showForgotPassword && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000 }}>
          <div data-testid="forgot-password-dialog" style={{
            background: "#1a1a2e", border: "2px solid #888", borderRadius: 8,
            padding: "28px 36px", maxWidth: 400,
            fontFamily: "'IBM Plex Sans', sans-serif",
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#FFF", marginBottom: 8, textAlign: "center" }}>
              Reset Password
            </div>
            <div style={{ fontSize: 13, color: "#AAA", marginBottom: 16, textAlign: "center" }}>
              Enter your User ID to reset your password to the default.
            </div>
            <div style={{ marginBottom: 16 }}>
              <input data-testid="forgot-userid" type="text" value={forgotUserid} onChange={(e) => setForgotUserid(e.target.value)} placeholder="Enter User ID"
                style={{ width: "100%", padding: "8px 12px", background: "#222", border: "1px solid #444", borderRadius: 4, color: "#FFF", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
            </div>
            {forgotError && <div style={{ color: "#FF4444", fontSize: 13, marginBottom: 10, textAlign: "center" }}>{forgotError}</div>}
            {forgotSuccess && <div style={{ color: "#00CC66", fontSize: 13, marginBottom: 10, textAlign: "center" }}>{forgotSuccess}</div>}
            <div style={{ display: "flex", gap: 12 }}>
              <button data-testid="submit-reset-password" onClick={handleForgotPassword}
                style={{ flex: 1, padding: "10px", background: "#00BFFF", color: "#000", border: "none", borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                Reset Password
              </button>
              <button onClick={() => { setShowForgotPassword(false); setForgotError(""); setForgotSuccess(""); }}
                style={{ flex: 1, padding: "10px", background: "#333", color: "#FFF", border: "1px solid #555", borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
